import { catalog } from './catalog.js'
import type { Completion, CompletionContext, CompletionResult, HoverInfo } from './types.js'
import { scanLine, wordBefore, wordAt } from './text.js'

/**
 * SQL completions for papan.db op files (`_db/*.sql`) and the schema (`_db/schema.sql`):
 *
 *   @|            → the server-bound context (`@actor`, `@now`, … — PII ones say so)
 *   :|            → the caller args this file already uses
 *   FROM |        → the tables in `_db/schema.sql`
 *   t.|           → the columns of the table `t` names in this statement
 *   anything else → keywords, the allowed functions, tables and columns
 *
 * Only functions papan.db ALLOWS are offered (anything else is refused when the
 * op loads), and every table carries the four system columns the engine adds.
 */

const KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'NULL', 'IS', 'IN', 'LIKE', 'GLOB', 'BETWEEN', 'EXISTS',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'RETURNING', 'ON CONFLICT', 'DO UPDATE', 'DO NOTHING',
  'OR IGNORE', 'OR REPLACE', 'JOIN', 'LEFT JOIN', 'INNER JOIN', 'CROSS JOIN', 'ON', 'USING', 'AS', 'DISTINCT',
  'GROUP BY', 'HAVING', 'ORDER BY', 'ASC', 'DESC', 'LIMIT', 'OFFSET', 'UNION', 'UNION ALL', 'WITH', 'CASE',
  'WHEN', 'THEN', 'ELSE', 'END', 'CAST', 'COLLATE NOCASE', 'TRUE', 'FALSE', 'CURRENT_TIMESTAMP',
]
const DDL_KEYWORDS = [
  'CREATE TABLE', 'CREATE TABLE IF NOT EXISTS', 'CREATE INDEX', 'CREATE UNIQUE INDEX', 'CREATE VIEW',
  'CREATE TRIGGER', 'IF NOT EXISTS', 'NOT NULL', 'DEFAULT', 'UNIQUE', 'CHECK', 'REFERENCES',
  'FOREIGN KEY', 'ON DELETE CASCADE', 'TEXT', 'INTEGER', 'REAL', 'BLOB', 'NUMERIC',
]

/** What each server-bound name holds — shown beside it. */
const CONTEXT_DOCS: Record<string, string> = {
  actor: 'The caller\'s pairwise actor id — the same id a `.py` script sees as `actor["id"]`.',
  permission: 'The caller\'s access on the app folder (`read`, `readwrite`, `admin`, …).',
  now: 'The current time (ISO timestamp), set by the server.',
  now_date: 'Today\'s date (`YYYY-MM-DD`), set by the server.',
  folderid: 'The app folder\'s id.',
  app: '1 when the op runs unattended as the app (a schedule or app hook), 0 for every user call — `WHERE @app = 1 OR …`.',
  email: 'The caller\'s email.',
  name: 'The caller\'s display name.',
  org: 'The caller\'s acting organization.',
  role: 'The caller\'s organization role.',
  teams: 'The caller\'s teams (a JSON tree — read it with `json_tree(@teams)`).',
}
/** The constants are lower-cased; show the spelling authors write. */
const CONTEXT_LABEL: Record<string, string> = { folderid: 'folderId' }

const SYSTEM_COLUMN_DOCS: Record<string, string> = {
  id: 'Primary key, added by the engine. Bind it yourself on insert: `uuid()`.',
  actor_id: 'Added by the engine. Bind it yourself on insert: `@actor`.',
  created_at: 'Set by the engine when the row is inserted.',
  updated_at: 'Set by the engine on every update.',
}

export interface SchemaTable { name: string; columns: string[] }

const CONSTRAINT_START = /^(PRIMARY|FOREIGN|UNIQUE|CHECK|CONSTRAINT)\b/i

/** Tables and columns declared in a schema (`CREATE TABLE` / `CREATE VIEW`), plus the system columns. */
export function parseSchema(sql: string): SchemaTable[] {
  const text = stripComments(sql)
  const tables: SchemaTable[] = []
  for (const m of text.matchAll(/\bCREATE\s+(?:TEMP\s+|TEMPORARY\s+)?TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?["`[]?([\w]+)["`\]]?\s*\(/gi)) {
    const bodyStart = m.index! + m[0].length
    const body = balancedBody(text, bodyStart)
    const columns: string[] = []
    for (const part of splitTopLevel(body)) {
      const def = part.trim()
      if (!def || CONSTRAINT_START.test(def)) continue
      const col = def.match(/^["`[]?([\w]+)["`\]]?/)
      if (col && !columns.includes(col[1]!)) columns.push(col[1]!)
    }
    for (const sys of catalog.sql.systemColumns) if (!columns.includes(sys)) columns.push(sys)
    tables.push({ name: m[1]!, columns })
  }
  for (const m of text.matchAll(/\bCREATE\s+VIEW\s+(?:IF\s+NOT\s+EXISTS\s+)?["`[]?([\w]+)/gi)) {
    if (!tables.some((t) => t.name === m[1])) tables.push({ name: m[1]!, columns: [] })
  }
  return tables
}

function stripComments(sql: string): string {
  return sql.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/--[^\n]*/g, ' ')
}

function balancedBody(text: string, start: number): string {
  let depth = 1
  for (let i = start; i < text.length; i++) {
    if (text[i] === '(') depth++
    else if (text[i] === ')' && --depth === 0) return text.slice(start, i)
  }
  return text.slice(start)
}

function splitTopLevel(body: string): string[] {
  const out: string[] = []
  let depth = 0
  let last = 0
  for (let i = 0; i < body.length; i++) {
    if (body[i] === '(') depth++
    else if (body[i] === ')') depth--
    else if (body[i] === ',' && depth === 0) { out.push(body.slice(last, i)); last = i + 1 }
  }
  out.push(body.slice(last))
  return out
}

/** The statement the caret is in: from the last `;` before it to the next `;` after it. */
function currentStatement(doc: string, pos: number): string {
  const start = doc.lastIndexOf(';', pos - 1) + 1
  const end = doc.indexOf(';', pos)
  return doc.slice(start, end < 0 ? doc.length : end)
}

/** Table name → alias map for the tables a statement names (`FROM tasks t`, `JOIN votes AS v`). */
function tableAliases(statement: string, tables: SchemaTable[]): Map<string, SchemaTable> {
  const byName = new Map(tables.map((t) => [t.name.toLowerCase(), t]))
  const out = new Map<string, SchemaTable>()
  for (const m of stripComments(statement).matchAll(/\b(?:FROM|JOIN|INTO|UPDATE)\s+["`[]?(\w+)["`\]]?(?:\s+(?:AS\s+)?(\w+))?/gi)) {
    const table = byName.get(m[1]!.toLowerCase())
    if (!table) continue
    out.set(table.name.toLowerCase(), table)
    const alias = m[2]
    if (alias && !/^(WHERE|SET|VALUES|ON|JOIN|LEFT|INNER|CROSS|GROUP|ORDER|LIMIT|USING|RETURNING|SELECT|DEFAULT)$/i.test(alias)) {
      out.set(alias.toLowerCase(), table)
    }
  }
  return out
}

function contextOptions(): Completion[] {
  return catalog.sql.context.map(({ name, pii }) => ({
    label: CONTEXT_LABEL[name] ?? name,
    type: 'constant' as const,
    detail: pii ? 'identity · needs consent' : 'server context',
    info: (CONTEXT_DOCS[name] ?? '') + (pii ? '\n\nConsent-gated: the manifest must list it in `wants_identity` (asks the person) or `wants_identity_optional` (NULL until granted).' : ''),
  }))
}

function columnOptions(table: SchemaTable): Completion[] {
  return table.columns.map((c) => ({
    label: c,
    type: 'property' as const,
    detail: table.name,
    info: SYSTEM_COLUMN_DOCS[c],
    boost: SYSTEM_COLUMN_DOCS[c] ? -1 : 0,
  }))
}

function tableOptions(tables: SchemaTable[]): Completion[] {
  return tables.map((t) => ({ label: t.name, type: 'class' as const, detail: 'table', info: t.columns.join(', ') }))
}

const isSchemaFile = (fileName: string) => /(^|\/)schema\.sql$/i.test(fileName)

export async function sqlCompletions(ctx: CompletionContext): Promise<CompletionResult | null> {
  const { doc, pos } = ctx
  const line = scanLine(doc, pos, '--')
  if (line.comment || line.stringStart !== null) return null
  const word = wordBefore(doc, pos, /\w/)
  const wordStart = pos - word.length
  const sigil = doc[wordStart - 1]

  if (sigil === '@') return { from: wordStart, options: contextOptions() }
  if (sigil === ':' && doc[wordStart - 2] !== ':') {
    const used = new Set<string>()
    for (const m of doc.matchAll(/(?<![:\w]):([A-Za-z_]\w*)/g)) if (m.index! + 1 !== wordStart) used.add(m[1]!)
    return { from: wordStart, options: [...used].map((p) => ({ label: p, type: 'variable' as const, detail: 'caller arg' })) }
  }

  const schemaText = isSchemaFile(ctx.fileName) ? doc : await ctx.app?.schemaSql() ?? ''
  const tables = parseSchema(schemaText)
  const statement = currentStatement(doc, pos)
  const aliases = tableAliases(statement, tables)

  if (sigil === '.') {
    const qualifier = wordBefore(doc, wordStart - 1, /\w/)
    const table = aliases.get(qualifier.toLowerCase()) ?? tables.find((t) => t.name.toLowerCase() === qualifier.toLowerCase())
    return table ? { from: wordStart, options: columnOptions(table) } : null
  }

  const before = doc.slice(Math.max(0, wordStart - 40), wordStart)
  if (/\b(FROM|JOIN|INTO|UPDATE|TABLE|REFERENCES)\s+$/i.test(before)) {
    return { from: wordStart, options: tableOptions(tables) }
  }

  if (!ctx.explicit && word.length < 1) return null
  const upper = word === word.toUpperCase() && /[A-Z]/.test(word)
  const kw = (k: string) => ({ label: upper || !word ? k : k.toLowerCase(), type: 'keyword' as const })
  const inStatement = [...new Set(aliases.values())]
  const options: Completion[] = [
    ...inStatement.flatMap(columnOptions).map((c) => ({ ...c, boost: (c.boost ?? 0) + 2 })),
    ...KEYWORDS.map(kw),
    ...(isSchemaFile(ctx.fileName) ? DDL_KEYWORDS.map(kw) : []),
    ...catalog.sql.functions.map((f) => ({ label: f, type: 'function' as const, detail: 'allowed function', boost: -1 })),
    ...tableOptions(tables),
    ...tables.filter((t) => !inStatement.includes(t)).flatMap(columnOptions).map((c) => ({ ...c, boost: (c.boost ?? 0) - 2 })),
  ]
  return { from: wordStart, options }
}

/** What hovering a name in SQL shows: `@context`, `:args`, tables, columns, allowed functions. */
export async function sqlHover(ctx: CompletionContext): Promise<HoverInfo | null> {
  const { doc, pos } = ctx
  if (scanLine(doc, pos + 1, '--').comment) return null
  const w = wordAt(doc, pos, /\w/)
  if (!w) return null
  const { from, to, word } = w
  const sigil = doc[from - 1]
  const lower = word.toLowerCase()
  if (sigil === '@') {
    const c = catalog.sql.context.find((x) => x.name === lower)
    if (!c) return { from, to, title: `@${word}`, doc: 'Not a server context name — the op will fail to load.' }
    const opt = contextOptions().find((o) => o.label.toLowerCase() === lower)!
    return { from, to, title: `@${opt.label}  ·  ${opt.detail}`, doc: opt.info ?? '' }
  }
  if (sigil === ':' && doc[from - 2] !== ':') return { from, to, title: `:${word}  ·  caller arg`, doc: `Bound from the args object: \`db.run(op, { ${word}: … })\`.` }
  const schemaText = isSchemaFile(ctx.fileName) ? doc : await ctx.app?.schemaSql() ?? ''
  const tables = parseSchema(schemaText)
  const table = tables.find((t) => t.name.toLowerCase() === lower)
  if (table) return { from, to, title: `table ${table.name}`, doc: table.columns.join(', ') }
  // A column: qualified (`t.col`) resolves through the statement's aliases.
  const aliases = tableAliases(currentStatement(doc, pos), tables)
  const qualifier = sigil === '.' ? wordBefore(doc, from - 1, /\w/).toLowerCase() : ''
  const owners = qualifier
    ? [aliases.get(qualifier) ?? tables.find((t) => t.name.toLowerCase() === qualifier)].filter((t): t is SchemaTable => !!t)
    : [...new Set([...aliases.values(), ...tables])]
  const owner = owners.find((t) => t.columns.some((c) => c.toLowerCase() === lower))
  if (owner) return { from, to, title: `${owner.name}.${word}`, doc: SYSTEM_COLUMN_DOCS[lower] ?? `Column of \`${owner.name}\`.` }
  if (doc[to] === '(' && catalog.sql.functions.includes(lower)) return { from, to, title: `${lower}()`, doc: 'Allowed in papan.db ops.' }
  return null
}
